/**
 * Projection for `PartsFlowScene` — the demo's `renderPartsflow()` (line 2788)
 * and `renderBom()` (line 2798).
 *
 * Pure: no React, no fetching, no engine mutation. The predicted-demand
 * arithmetic is `predictedUsage()` in `shared/oem-engine.ts`; this file sorts
 * orders into pipeline columns, groups stock by location, and narrows the parts
 * books to the model family in view.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/service/PartsFlowScene.tsx
 * @see docs/logic/workflows/parts-flow.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { BY_ID, MINES } from '../../data/core';
import type { LakeEntry } from '../../schema/document';
import type { ModelCode } from '../../schema/machine';
import type {
  CataloguePart,
  CatbaseBomLine,
  CatbaseSection,
  InventoryLine,
  ModelFamily,
  PartOrder,
} from '../../shared/useOemData';

// ---------------------------------------------------------------------------
// The order pipeline
// ---------------------------------------------------------------------------

/** Demo line 2788. The four pipeline columns, in order. */
export const PIPELINE_COLUMNS = [
  ['intake', 'Order intake'],
  ['processing', 'Processing'],
  ['transit', 'In transit'],
  ['delivered', 'Delivered'],
] as const satisfies readonly (readonly [string, string])[];

/** A pipeline column key. */
export type PipelineColumn = (typeof PIPELINE_COLUMNS)[number][0];

/** Demo line 2788. Orders from this date are in the pipeline view. */
export const PIPELINE_FROM = '2026-08-20';

/** Demo line 2788. A column shows twelve cards before it collapses. */
export const PIPELINE_CARD_LIMIT = 12;

/**
 * Demo line 2788. `col(o)` — which column an order sits in.
 *
 * The status field is free text carrying the freight mode, the source DC and
 * the ETA, so the demo sorts on four regexes in a fixed order. Ported verbatim,
 * including the overlap: an `'Ordered'` row that is neither delivered nor on
 * air matches `processing` first and never reaches the `transit` clause.
 */
export function pipelineColumn(order: Pick<PartOrder, 'status'>): PipelineColumn {
  const s = order.status;
  if (/Draft/.test(s)) return 'intake';
  if (/awaiting|Ordered|reserved|No order/i.test(s) && !/Delivered/.test(s) && !/air|ETA/i.test(s)) {
    return 'processing';
  }
  if (/air|ETA|Ordered/i.test(s) && !/Delivered/.test(s)) return 'transit';
  return 'delivered';
}

/** Demo line 2790. One order card. */
export interface OrderCard {
  readonly order: PartOrder;
  /** Catalogue description, `''` when the part is not in the catalogue. */
  readonly description: string;
  /** `'1 × A$2,650'`, or just the quantity where there is no list price. */
  readonly priceLine: string;
  /** Short site name — the demo prints the first word. */
  readonly siteShort: string;
  /** Drafted by the Parts sales agent: the card takes the accent edge. */
  readonly byAgent: boolean;
  /** Air freight or urgent: the card takes the critical edge. */
  readonly urgent: boolean;
  readonly delivered: boolean;
  /** Drafts carry an Approve button. */
  readonly approvable: boolean;
}

/** One pipeline column with its cards. */
export interface PipelineColumnView {
  readonly key: PipelineColumn;
  readonly label: string;
  readonly total: number;
  readonly cards: readonly OrderCard[];
  /** Orders beyond {@link PIPELINE_CARD_LIMIT}. */
  readonly overflow: number;
}

/** What the pipeline tab needs loaded. */
export interface PartsCtx {
  readonly orders: readonly PartOrder[];
  readonly parts: readonly CataloguePart[];
  readonly inventory: readonly InventoryLine[];
  readonly catbaseBom: readonly CatbaseBomLine[];
  readonly catbaseSections: readonly CatbaseSection[];
}

/** Demo line 2790. The card's derived figures, no markup. */
function orderCard(order: PartOrder, parts: readonly CataloguePart[]): OrderCard {
  const part = parts.find((p) => p.part_no === order.part_no);
  const machine = BY_ID[order.machine_id];
  const qty = order.qty || 1;
  return {
    order,
    description: part?.description ?? '',
    priceLine: part?.list_price_aud
      ? `${qty} × A$${part.list_price_aud.toLocaleString('en-AU')}`
      : `${qty} ×`,
    siteShort: machine ? MINES[machine.mine].name.split(' ')[0] : '',
    byAgent: /agent/i.test(order.status),
    urgent: /air|urgent/i.test(order.status),
    delivered: /Delivered/.test(order.status),
    approvable: /Draft/.test(order.status),
  };
}

/** Demo line 2788. The pipeline, newest order first within each column. */
export function pipeline(ctx: PartsCtx): PipelineColumnView[] {
  const scope = ctx.orders.filter((o) => o.order_date >= PIPELINE_FROM);
  return PIPELINE_COLUMNS.map(([key, label]) => {
    const column = scope
      .filter((o) => pipelineColumn(o) === key)
      .slice()
      .sort((a, b) => b.order_date.localeCompare(a.order_date));
    return {
      key,
      label,
      total: column.length,
      cards: column.slice(0, PIPELINE_CARD_LIMIT).map((o) => orderCard(o, ctx.parts)),
      overflow: Math.max(0, column.length - PIPELINE_CARD_LIMIT),
    };
  });
}

// ---------------------------------------------------------------------------
// Inventory control
// ---------------------------------------------------------------------------

/** Demo line 2794. One stock location's tile. */
export interface LocationStock {
  readonly location: string;
  readonly lines: readonly InventoryLine[];
  readonly belowMin: readonly InventoryLine[];
  /** The first four below-minimum part numbers, as the tile's detail line. */
  readonly examples: string;
  readonly more: boolean;
}

/** Demo line 2794. Every stock location, in the order the file lists them. */
export function locationStock(ctx: PartsCtx): LocationStock[] {
  const locations = [...new Set(ctx.inventory.map((i) => i.location))];
  return locations.map((location) => {
    const lines = ctx.inventory.filter((i) => i.location === location);
    const belowMin = lines.filter((i) => i.qty_on_hand < i.min_level);
    return {
      location,
      lines,
      belowMin,
      examples: belowMin.slice(0, 4).map((i) => i.part_no).join(' · '),
      more: belowMin.length > 4,
    };
  });
}

/** One below-minimum line with its catalogue detail joined on. */
export interface BelowMinRow extends InventoryLine {
  readonly description: string;
  readonly criticality: string;
  readonly leadTimeDays: number | null;
  /** Stable key: a part number repeats across locations. */
  readonly key: string;
}

/** Demo line 2794. Every below-minimum line, across every location. */
export function belowMinimum(ctx: PartsCtx): BelowMinRow[] {
  return ctx.inventory
    .filter((i) => i.qty_on_hand < i.min_level)
    .map((i) => {
      const part = ctx.parts.find((p) => p.part_no === i.part_no);
      return {
        ...i,
        description: part?.description ?? '',
        criticality: part?.criticality ?? '',
        leadTimeDays: part?.lead_time_days ?? null,
        key: `${i.location}|${i.part_no}`,
      };
    });
}

// ---------------------------------------------------------------------------
// The digital parts books
// ---------------------------------------------------------------------------

/** Demo line 2796. The model families in the BOM export, in file order. */
export const bomFamilies = (ctx: PartsCtx): ModelFamily[] => [
  ...new Set(ctx.catbaseBom.map((b) => b.model_family)),
];

/**
 * A model family's member models.
 *
 * The BOM export is published per **family** (`'MT-50/63'`) and the section
 * books per **model** (`'MT-63'`), so the two only join through this table. It
 * is derived from the two published vocabularies rather than parsed out of the
 * family string, because `'ML-21 series'` covers `ML-21E` and `'MP-60/60A'`
 * covers only `MP-60`.
 */
export const FAMILY_MODELS: Readonly<Record<ModelFamily, readonly ModelCode[]>> = {
  'ML-21 series': ['ML-21', 'ML-21E'],
  'MJ-2D': ['MJ-2D'],
  'MT-50/63': ['MT-50', 'MT-63'],
  'MP-60/60A': ['MP-60'],
  'MS-45/120': ['MS-45', 'MS-120'],
  // The fleet-wide consumables. No book of their own; every model carries them.
  All: [],
};

/** Demo line 2798. One BOM export line, with its stock joined on. */
export interface BomRow extends CatbaseBomLine {
  /** Stock across every location. */
  readonly stock: number;
  /** Stable key: `item_no` repeats across sections. */
  readonly key: string;
}

/**
 * Demo line 2798. `renderBom()` — the BOM export for one family, optionally
 * narrowed by a free-text filter.
 *
 * The filter matches the demo's: the whole row serialised, lower-cased and
 * searched, so a query hits a part number, a description, a section or a price.
 */
export function bomRows(ctx: PartsCtx, family: ModelFamily, query: string): BomRow[] {
  const q = query.trim().toLowerCase();
  return ctx.catbaseBom
    .filter((b) => b.model_family === family)
    .filter((b) => !q || JSON.stringify(b).toLowerCase().includes(q))
    .map((b) => ({
      ...b,
      stock: ctx.inventory
        .filter((i) => i.part_no === b.part_no)
        .reduce((a, i) => a + i.qty_on_hand, 0),
      key: `${b.section}|${b.item_no}|${b.part_no}`,
    }));
}

/** One published section of a model's parts book. */
export interface BookSection {
  readonly model: ModelCode;
  readonly section: number;
  readonly title: string;
  readonly lines: readonly CatbaseSection[];
  readonly key: string;
}

/**
 * The `catbase_sections` rows for a family's models, grouped into the sections
 * the book publishes — the level a technician quotes ("item 3 on section 07").
 */
export function bookSections(ctx: PartsCtx, family: ModelFamily): BookSection[] {
  const models = FAMILY_MODELS[family] ?? [];
  const wanted = ctx.catbaseSections.filter((s) => models.includes(s.model));
  const keys = [...new Set(wanted.map((s) => `${s.model}|${s.section}`))];
  return keys.map((key) => {
    const lines = wanted
      .filter((s) => `${s.model}|${s.section}` === key)
      .slice()
      .sort((a, b) => a.item_no - b.item_no);
    return {
      model: lines[0].model,
      section: lines[0].section,
      title: lines[0].section_title,
      lines,
      key,
    };
  });
}

/** Demo line 2796. One parts-book PDF in the lake. */
export interface PartsBook {
  readonly entry: LakeEntry;
  /** `'MS-120 Parts-Book S07 Drill-String'` — the demo's file-name clean-up. */
  readonly label: string;
  /** Model the book is published for, taken from the file-name prefix. */
  readonly model: string;
}

/**
 * Demo line 2796. The parts-book PDFs for a family, out of the lake catalogue.
 *
 * The demo lists all seventeen books on every tab render and decodes seventeen
 * thumbnails with them. The port narrows to the family in view, which is the
 * same set the reader is looking at the BOM for and keeps the tab to two or
 * three document fetches instead of seventeen.
 */
export function partsBooks(entries: readonly LakeEntry[], family: ModelFamily): PartsBook[] {
  const models = FAMILY_MODELS[family] ?? [];
  return entries
    .filter((e) => e.folder === '14_catbase' && e.kind === 'pdf')
    .map((entry) => ({
      entry,
      label: entry.file.replace(/_/g, ' ').replace('.pdf', ''),
      model: entry.file.split('_')[0],
    }))
    .filter((b) => models.includes(b.model as ModelCode));
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/** Demo line 2791. The four tabs, in the demo's order. */
export const PARTS_FLOW_TABS = [
  ['pipeline', 'Order pipeline'],
  ['predict', 'Predictive usage → minimums'],
  ['inventory', 'Inventory control'],
  ['bom', 'BOM lookup (CatBase)'],
] as const satisfies readonly (readonly [string, string])[];

/** A parts-flow tab key. */
export type PartsFlowTab = (typeof PARTS_FLOW_TABS)[number][0];

/** Whether a URL value names a tab. */
export const isPartsFlowTab = (value: string | null): value is PartsFlowTab =>
  PARTS_FLOW_TABS.some(([key]) => key === value);
